import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/shared/api/useApi';
import type { PluginConnectionCheck } from '@/shared/api/types/plugins';
import { Button, Input, Label } from '@/shared/components';

const checkText: Record<PluginConnectionCheck['status'], string> = {
  Connected: 'Verbunden',
  InvalidKey: 'Der Schlüssel ist ungültig oder wurde widerrufen.',
  Unreachable: 'GuestAI ist gerade nicht erreichbar.',
  NotConfigured: 'Die Verbindung wurde noch nicht vom Betreiber konfiguriert.',
  NoKey: 'Es ist noch kein Schlüssel hinterlegt.',
};

// Write-only: a stored key is never shown again, only its prefix.
export function PluginConnectionKeyCard({ pluginId }: { pluginId: string }) {
  const api = useApi();
  const client = useQueryClient();
  const queryKey = ['plugin-connection-key', pluginId];
  const [value, setValue] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const current = useQuery({
    queryKey,
    queryFn: () => api.plugins.connectionKey(pluginId),
    retry: false,
  });
  const check = useMutation({ mutationFn: () => api.plugins.checkConnectionKey(pluginId) });
  const refresh = (data: Awaited<ReturnType<typeof api.plugins.connectionKey>>) => {
    client.setQueryData(queryKey, data);
    check.reset();
  };
  const save = useMutation({
    mutationFn: () => api.plugins.saveConnectionKey(pluginId, value.trim()),
    onSuccess: (data) => {
      setValue('');
      refresh(data);
    },
  });
  const remove = useMutation({
    mutationFn: () => api.plugins.removeConnectionKey(pluginId),
    onSuccess: (data) => {
      setConfirmRemove(false);
      refresh(data);
    },
  });
  const key = current.data;
  return (
    <section
      className="border-line space-y-3 rounded-lg border p-4"
      aria-label="GuestAI-Verbindungsschlüssel"
    >
      <h2 className="font-serif text-xl">GuestAI-Verbindungsschlüssel</h2>
      <p className="text-ink-soft text-sm">
        Den Schlüssel erzeugt ein Admin in GuestAI unter Einstellungen. Er wird verschlüsselt
        gespeichert und danach nie wieder angezeigt.
      </p>
      {current.isError && <p role="alert">Der Schlüsselstand ist nicht erreichbar.</p>}
      {key && (
        <p className="text-sm">
          {key.hasKey
            ? `Hinterlegt: ${key.prefix}… seit ${new Date(key.savedAt ?? '').toLocaleString('de-DE')}`
            : 'Noch kein Schlüssel hinterlegt.'}
        </p>
      )}
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (value.trim()) save.mutate();
        }}
      >
        <div>
          <Label htmlFor={`plugin-key-${pluginId}`}>
            {key?.hasKey ? 'Neuen Schlüssel eintragen' : 'Schlüssel eintragen'}
          </Label>
          <Input
            id={`plugin-key-${pluginId}`}
            type="password"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            autoComplete="new-password"
            spellCheck={false}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            type="submit"
            size="lg"
            variant="primary"
            disabled={!value.trim()}
            loading={save.isPending}
          >
            Speichern
          </Button>
          {key?.hasKey && (
            <Button
              type="button"
              size="lg"
              loading={check.isPending}
              onClick={() => check.mutate()}
            >
              Verbindung prüfen
            </Button>
          )}
          {key?.hasKey && !confirmRemove && (
            <Button
              type="button"
              size="lg"
              variant="destructive"
              onClick={() => setConfirmRemove(true)}
            >
              Schlüssel entfernen
            </Button>
          )}
          {key?.hasKey && confirmRemove && (
            <>
              <Button
                type="button"
                size="lg"
                variant="destructive"
                loading={remove.isPending}
                onClick={() => remove.mutate()}
              >
                Entfernen bestätigen
              </Button>
              <Button type="button" size="lg" onClick={() => setConfirmRemove(false)}>
                Abbrechen
              </Button>
            </>
          )}
        </div>
      </form>
      {save.isError && (
        <p role="alert">Der Schlüssel wurde nicht gespeichert. Bitte das Format prüfen.</p>
      )}
      {(remove.isError || check.isError) && <p role="alert">Die Aktion ist fehlgeschlagen.</p>}
      {check.data && (
        <p role="status">
          {check.data.status === 'Connected'
            ? `Verbunden mit GuestAI-Workspace „${check.data.workspaceName}“.`
            : checkText[check.data.status]}
        </p>
      )}
    </section>
  );
}
